import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useState } from "react";

/**
 * The frame every screen lives in: sidebar navigation, top bar, and the routed outlet.
 *
 * <p>Matches the sidebar in the Stitch mockups. Collapses below the medium breakpoint so the panel
 * remains usable on a tablet, which is a realistic place for an operator to check on a campaign.
 */

const NAV_ITEMS = [
  { to: "/", label: "Panel", end: true },
  { to: "/campaigns", label: "Campañas" },
  { to: "/advertisers", label: "Anunciantes" },
  { to: "/apps", label: "Apps" },
  { to: "/mediation", label: "Mediación" },
  { to: "/reports", label: "Reportes" },
];

export function Shell() {
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-surface-primary">
      <div className="flex">
        <Sidebar open={navOpen} onNavigate={() => setNavOpen(false)} />

        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onToggleNav={() => setNavOpen((open) => !open)} />

          <main className="flex-1 p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

function Sidebar({
  open,
  onNavigate,
}: {
  open: boolean;
  onNavigate: () => void;
}) {
  return (
    <aside
      className={[
        "w-56 shrink-0 border-r border-surface-tertiary bg-surface-secondary p-4",
        // Hidden on narrow screens until opened, always visible from md up.
        open ? "fixed inset-y-0 left-0 z-40 block" : "hidden",
        "md:sticky md:top-0 md:block md:h-screen",
      ].join(" ")}
    >
      <div className="mb-8 px-2">
        <p className="text-headline-sm text-text-primary">D&apos;RAMYERI SW</p>
        <p className="font-mono text-label-md uppercase text-text-secondary">
          Ad Server
        </p>
      </div>

      <nav className="flex flex-col gap-1">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                "rounded px-3 py-2 text-body-md transition",
                isActive
                  ? "bg-electric-blue/15 text-text-primary"
                  : "text-text-secondary hover:bg-surface-tertiary hover:text-text-primary",
              ].join(" ")
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

function TopBar({ onToggleNav }: { onToggleNav: () => void }) {
  const auth = useAuth();
  const name =
    auth.user?.profile.name ??
    auth.user?.profile.preferred_username ??
    "Operador";

  return (
    <header className="flex items-center justify-between gap-4 border-b border-surface-tertiary bg-surface-secondary px-6 py-3">
      <button
        onClick={onToggleNav}
        className="rounded px-2 py-1 text-text-secondary hover:text-text-primary md:hidden"
        aria-label="Alternar navegación"
      >
        ☰
      </button>

      <div className="ml-auto flex items-center gap-4">
        <span className="text-body-md text-text-secondary">{name}</span>
        <button
          onClick={() => {
            // Ends the Keycloak session too, not just this tab's token — otherwise signing out
            // and back in would silently reuse the same session.
            void auth.signoutRedirect();
          }}
          className="rounded px-3 py-1.5 text-body-md text-text-secondary hover:bg-surface-tertiary hover:text-text-primary"
        >
          Salir
        </button>
      </div>
    </header>
  );
}
